import React, { Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, FileCheck, Building2, Code } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import LoadingSpinner from '../common/LoadingSpinner';
import FeatureCard from '../common/FeatureCard';
import FAQItem from '../common/FAQItem';
import YouTubeLazy from '../common/YouTubeLazy';
import BlogPreview from '../blog/BlogPreview';
import { SEOHead } from '../common/SEOHead';
import { TranslationStep } from '../../types';

/**
 * Home page component displaying the landing page content
 */
const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const handleNavigation = (path: string) => {
    navigate(`/${language}${path}`);
  };

  return (
    <article className="min-h-screen bg-[var(--bg-primary)]" role="main">
      <SEOHead 
        titleKey="home.pageTitle"
        descriptionKey="home.description"
        additionalMeta={[
          { 
            name: "viewport", 
            content: "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" 
          }
        ]}
      />
      <Header />
      <main>
        {/* Hero Section - Adjust height and overflow handling */}
        <div className="relative min-h-[400px] overflow-visible">
          <div className="absolute inset-0 bg-[var(--brand-primary)]/5 backdrop-blur-3xl"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Promo banner temporarily hidden
            {!user && (
              <div
                className="h-12 mb-4"
                aria-hidden={!user}
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 top-12 inline-flex px-6 py-3 bg-[var(--brand-primary)]/20 border border-[var(--brand-primary)] rounded-full w-fit">
                  <p className="text-sm font-medium text-[var(--brand-primary)] whitespace-nowrap">
                    <span className="hidden sm:inline">
                      {t('hero.promo')}
                    </span>
                    <span className="sm:hidden inline-flex items-center gap-1">
                      {t('hero.promoMobile')}
                    </span>
                  </p>
                </div>
              </div>
            )}
            */}
            <div className="text-center">
              <div className="relative mb-4">
                <h1 className="text-4xl font-bold text-[var(--text-primary)] sm:text-5xl md:text-6xl mt-8">
                  {t('hero.title')} <span className="text-[var(--brand-primary)]">{t('hero.titleHighlight')}</span> {t('hero.titleEnd')}
                </h1>
              </div>
              <p className="mt-4 max-w-3xl mx-auto text-lg text-[var(--text-tertiary)]">
                {t('hero.subtitle')}
              </p>
              <div className="mt-6 pb-8">
                {user ? (
                  <div className="flex justify-center">
                    <button
                      onClick={() => handleNavigation('/pdf-to-xrechnung')}
                      className="bg-[var(--brand-primary)] text-[var(--text-button)] inline-flex items-center px-3 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-colors shadow-brand"
                    >
                      {t('hero.convertNow')}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <div className="flex justify-center">
                      <button
                        onClick={() => handleNavigation('/pdf-to-xrechnung')}
                        className="bg-[var(--brand-primary)] text-[var(--text-button)] inline-flex items-center px-3 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-colors shadow-brand"
                      >
                        {t('nav.convert')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Demo Video Section */}
        <div className="py-4 bg-[var(--brand-primary)]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative w-full max-w-4xl mx-auto shadow-[0_0_20px_rgba(var(--rgb-text-primary),0.1)]">
              <YouTubeLazy
                videoId="HszlRfheBAY"
                title="Converting between PDF and XRechnung/XML invoices in seconds"
              />
            </div>
          </div>
        </div>

        {/* Enhanced Features Grid */}
        <Suspense fallback={<LoadingSpinner />}>
          <section className="py-16" aria-labelledby="features-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 id="features-heading" className="text-3xl font-bold text-[var(--text-primary)]">
                  {t('features.heading')} <span className="text-[var(--brand-primary)]">{t('features.headingHighlight')}</span>
                </h2>
                <p className="mt-4 text-[var(--text-tertiary)] max-w-2xl mx-auto">
                  {t('features.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" role="list">
                {[
                  { icon: <FileCheck className="w-8 h-8 text-[var(--brand-primary)]" />, title: 'features.ai.title', description: 'features.ai.description' },
                  { icon: <Shield className="w-8 h-8 text-[var(--brand-primary)]" />, title: 'features.secure.title', description: 'features.secure.description' },
                  { icon: <Building2 className="w-8 h-8 text-[var(--brand-primary)]" />, title: 'features.compliance.title', description: 'features.compliance.description' },
                  { icon: <Code className="w-8 h-8 text-[var(--brand-primary)]" />, title: 'features.compatibility.title', description: 'features.compatibility.description' }
                ].map((feature, index) => (
                  <div key={index} role="listitem" className="flex">
                    <FeatureCard
                      icon={feature.icon}
                      title={t(feature.title)}
                      description={t(feature.description)}
                      className="flex-1 flex flex-col"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Suspense>

        {/* How It Works */}
        <Suspense fallback={
          <div className="bg-[var(--brand-primary)]/5 py-24 min-h-[600px] animate-pulse">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="relative p-6 text-center">
                    <div className="bg-[var(--bg-tertiary)] h-12 w-12 rounded-full mx-auto mb-4"></div>
                    <div className="bg-[var(--bg-tertiary)] h-6 w-3/4 mx-auto mb-2"></div>
                    <div className="bg-[var(--bg-tertiary)] h-4 w-full"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        }>
          <section className="bg-[var(--brand-primary)]/5 py-24" aria-labelledby="how-it-works-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 id="how-it-works-heading" className="text-3xl font-bold text-[var(--text-primary)]">
                  {t('howItWorks.title')} <span className="text-[var(--brand-primary)]">{t('howItWorks.titleHighlight')}</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {(t<TranslationStep[]>('howItWorks.steps')).map((item) => (
                  <div key={item.step} className="relative p-6 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--brand-primary)] text-[var(--text-button)] font-bold mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">{item.title}</h3>
                    <p className="text-[var(--text-tertiary)]">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Suspense>

        {/* Custom Pricing Section */}
        <div id="pricing" className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[var(--text-primary)]">
                {t('pricing.title')} <span className="text-[var(--brand-primary)]">{t('pricing.titleHighlight')}</span>
              </h2>
              <p className="mt-4 text-[var(--text-tertiary)]">
                {t('pricing.subtitle')}
              </p>
            </div>
            <div className="max-w-lg mx-auto">
              {/* Annual Plan */}
              <div className="p-8 rounded-xl border border-[var(--brand-primary)]/20 bg-[var(--brand-primary)]/20 hover:border-[var(--brand-primary)]/40 transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-[var(--text-primary)]">{t('pricing.annual')}</h3>
                  <div className="px-3 py-1 bg-[var(--brand-primary)]/20 rounded-full">
                    <span className="text-[var(--brand-primary)] text-sm font-medium">{t('pricing.save')}</span>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-[var(--text-tertiary)] line-through">{t('pricing.originalPrice')}</span>
                  <span className="text-4xl font-bold text-[var(--brand-primary)] ml-3">{t('pricing.monthlyPrice')}</span>
                  <span className="text-[var(--text-tertiary)] ml-2">{t('pricing.perMonth')}</span>
                </div>
                <p className="text-sm text-[var(--text-tertiary)] mb-6">
                  <span className="line-through">{t('pricing.yearlyOriginal')}</span>
                  <span className="text-[var(--brand-primary)] font-bold ml-2">{t('pricing.yearlyDiscount')}</span>
                </p>
                <div className="bg-[var(--brand-primary)]/10 border border-[var(--brand-primary)]/30 rounded-lg p-4 mb-8">
                  <p className="text-[var(--brand-primary)] font-medium">{t('pricing.launchOffer')}</p>
                  <p className="text-[var(--text-tertiary)] text-sm mt-1">
                    {t('pricing.launchDesc')}
                    <span className="text-[var(--brand-primary)] font-bold">
                      &nbsp;{t('pricing.launchCode')}
                    </span>
                  </p>
                  <p className="text-[var(--text-tertiary)] text-sm">{t('pricing.launchLimit')}</p>
                </div>
                <ul className="space-y-4 mb-8">
                  {(t<string[]>('pricing.features')).map((feature) => (
                    <li key={feature} className="flex items-center text-[var(--text-tertiary)]">
                      <svg className="w-5 h-5 text-[var(--brand-primary)] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to={`/${language}/sign-up`} 
                  className="block w-full px-6 py-3 bg-[var(--brand-primary)] text-[var(--text-button)] font-semibold rounded-lg hover:bg-[var(--brand-hover)] transition-all duration-300 text-center"
                >
                  {t('pricing.cta')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* New Blog Section */}
        <Suspense fallback={<LoadingSpinner />}>
          <div className="bg-[var(--brand-primary)]/5 py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-[var(--text-primary)]">
                  <Link
                    to={`/${language}/blog`}
                    className="hover:opacity-80"
                  >
                    {t('blog.heading')} <span className="text-[var(--brand-primary)]">{t('blog.headingHighlight')}</span>
                  </Link>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <BlogPreview
                  title={t('blog.posts.zugferd-vs-xrechnung.title')}
                  date={t('blog.posts.zugferd-vs-xrechnung.date')}
                  to={`/blog/zugferd-vs-xrechnung`}
                />
                <BlogPreview
                  title={t('blog.posts.eu-einvoicing-2025.title')}
                  date={t('blog.posts.eu-einvoicing-2025.date')}
                  to={`/blog/eu-einvoicing-2025`}
                />
                <BlogPreview
                  title={t('blog.posts.xrechnung-guide-2025.title')}
                  date={t('blog.posts.xrechnung-guide-2025.date')}
                  to={`/blog/xrechnung-guide-2025`}
                />
              </div>
            </div>
          </div>
        </Suspense>

        {/* FAQ Section */}
        <Suspense fallback={<LoadingSpinner />}>
          <div className="py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-[var(--text-primary)]">
                  {t('faq.title')} <span className="text-[var(--brand-primary)]">{t('faq.titleHighlight')}</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {(t<Array<{ question: string; answer: string }>>('faq.items')).map((item, index) => (
                  <FAQItem
                    key={`${language}-faq-${index}-${item.question}`}
                    question={item.question}
                    answer={item.answer}
                  />
                ))}
              </div>
            </div>
          </div>
        </Suspense>

        {/* CTA Section */}
        <div className="bg-[var(--brand-primary)]/5 py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-[var(--text-tertiary)] mb-8 max-w-2xl mx-auto">
              {t('cta.subtitle')}
            </p>
            <Link
              to={`/${language}/pdf-to-xrechnung`}
              className="inline-block px-8 py-4 bg-[var(--brand-primary)] text-[var(--text-button)] font-semibold rounded-lg hover:opacity-90 transition-all duration-300 shadow-brand"
            >
              {t('cta.button')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </article>
  );
};

export default Home;